import React, { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { FieldDef } from '../../orbit/lib/dossier-types';
import { AddFieldControl } from './AddFieldControl';

/**
 * The step before the problem: anything we never asked for.
 *
 * Four cards can only cover what the registry already knows to ask. This is
 * where a company puts the thing that matters to them and nowhere fits — and
 * because it goes through the same added-field path as the per-card control, it
 * lands as a properly named field on the right card rather than in a notes box
 * nothing reads.
 */

const SECTION_CHOICES: { value: string; label: string }[] = [
  { value: 'a', label: 'The company' },
  { value: 'b', label: 'Tools' },
  { value: 'c', label: 'Teams' },
  { value: 'd', label: 'Goals' },
];

export interface CatchAllCardProps {
  fields: FieldDef[];
  clientId?: string;
  onAdded: (key: string) => void;
}

export const CatchAllCard: React.FC<CatchAllCardProps> = ({ fields, clientId, onAdded }) => {
  const [section, setSection] = useState('a');

  // Everything this company has added so far, so the step shows what it
  // collected rather than looking like nothing happened. Read from the
  // registry's own `added` flag — low leverage alone would also match plenty
  // of default fields.
  const added = useMemo(() => fields.filter(f => f.added), [fields]);

  const sectionName = (letter: string) =>
    SECTION_CHOICES.find(s => s.value === letter)?.label ?? letter.toUpperCase();

  return (
    <section className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200/70">
      <header className="px-5 pt-5">
        <div className="flex items-start gap-3">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-600" aria-hidden="true">
            <Sparkles size={16} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold tracking-tight text-gray-900">
              Anything we missed?
            </h2>
            <p className="mt-0.5 text-[12.5px] leading-snug text-gray-500">
              Something about your business that matters and we never asked about.
              Skip this if nothing comes to mind.
            </p>
          </div>
        </div>
      </header>

      <div className="px-5 py-4">
        <AddFieldControl
          section={section}
          clientId={clientId}
          onAdded={onAdded}
          label="Add something we missed"
          sectionPicker={SECTION_CHOICES}
          onSectionChange={setSection}
        />
      </div>

      {added.length > 0 && (
        <div className="border-t border-gray-100 px-5 py-4">
          <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            You added
          </span>
          <ul className="flex flex-col gap-1.5">
            {added.map(f => (
              <li key={f.key} className="flex items-baseline gap-2 text-[13px]">
                <span className="font-medium text-gray-900">{f.label}</span>
                <span className="text-[11.5px] text-gray-400">
                  on {sectionName(f.section)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default CatchAllCard;
